// src/pages/debug/CodeDebugPage.tsx
// صفحة تشخيص وإصلاح أكواد الطلاب للمطورين

import React, { useState } from 'react';
import EnhancedCodeValidation from '../../services/enhancedCodeValidation';
import EmergencyCodeFix from '../../services/emergencyCodeFix';

const CodeDebugPage: React.FC = () => {
  const [code, setCode] = useState('S-2JYBAVWZ');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [fixResults, setFixResults] = useState<any>(null);

  const testCode = async () => {
    setLoading(true);
    try {
      console.log('🔍 اختبار الكود:', code);

      // 1. اختبار النظام المحسن
      const validationResult = await EnhancedCodeValidation.validateStudentCode(
        code
      );

      // 2. اختبار حالة الكود
      const testResult = await EmergencyCodeFix.testFixedCode(code);

      setResults({
        validation: validationResult,
        test: testResult,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('خطأ في الاختبار:', error);
      setResults({
        error: error instanceof Error ? error.message : 'خطأ غير معروف',
      });
    } finally {
      setLoading(false);
    }
  };

  const fixCode = async () => {
    setLoading(true);
    try {
      console.log('🔧 إصلاح الكود:', code);

      const fixResult = await EmergencyCodeFix.fixSpecificCode(code);

      setFixResults({
        fix: fixResult,
        timestamp: new Date().toISOString(),
      });

      // إعادة الاختبار بعد الإصلاح
      if (fixResult.success) {
        setTimeout(() => {
          testCode();
        }, 1000);
      }
    } catch (error) {
      console.error('خطأ في الإصلاح:', error);
      setFixResults({
        error: error instanceof Error ? error.message : 'خطأ غير معروف',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gray-50 p-8'>
      <div className='max-w-4xl mx-auto'>
        <div className='bg-white rounded-lg shadow-lg p-6'>
          <h1 className='text-2xl font-bold text-gray-800 mb-6'>
            🔧 صفحة تشخيص أكواد الطلاب
          </h1>

          <div className='mb-6'>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              كود الطالب للاختبار
            </label>
            <div className='flex gap-4'>
              <input
                type='text'
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                className='flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500'
                placeholder='أدخل كود الطالب (مثل: S-2JYBAVWZ)'
              />
              <button
                onClick={testCode}
                disabled={loading}
                className='px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50'
              >
                {loading ? '🔄 جاري الاختبار...' : '🔍 اختبار الكود'}
              </button>
              <button
                onClick={fixCode}
                disabled={loading}
                className='px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50'
              >
                {loading ? '🔄 جاري الإصلاح...' : '🔧 إصلاح الكود'}
              </button>
            </div>
          </div>

          {/* نتائج الاختبار */}
          {results && (
            <div className='mb-6'>
              <h2 className='text-lg font-semibold text-gray-800 mb-4'>
                📊 نتائج الاختبار
              </h2>

              {results.error ? (
                <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                  <p className='text-red-800'>❌ خطأ: {results.error}</p>
                </div>
              ) : (
                <div className='space-y-4'>
                  {/* حالة التحقق */}
                  <div
                    className={`border rounded-lg p-4 ${
                      results.validation?.isValid
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <h3 className='font-medium mb-2'>
                      {results.validation?.isValid
                        ? '✅ التحقق: صحيح'
                        : '❌ التحقق: خطأ'}
                    </h3>
                    {results.validation?.error && (
                      <p className='text-sm text-red-600'>
                        الخطأ: {results.validation.error}
                      </p>
                    )}
                    {results.validation?.source && (
                      <p className='text-sm text-green-600'>
                        المصدر: {results.validation.source}
                      </p>
                    )}
                    {results.validation?.studentData && (
                      <p className='text-sm text-blue-600'>
                        الطالب:{' '}
                        {results.validation.studentData.name || 'غير محدد'}
                      </p>
                    )}
                  </div>

                  {/* حالة المجموعات */}
                  <div className='grid grid-cols-3 gap-4'>
                    <div
                      className={`border rounded-lg p-4 text-center ${
                        results.test?.linkingCodeExists
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <h4 className='font-medium text-sm'>linkingCodes</h4>
                      <p className='text-lg'>
                        {results.test?.linkingCodeExists ? '✅' : '❌'}
                      </p>
                    </div>
                    <div
                      className={`border rounded-lg p-4 text-center ${
                        results.test?.studentExists
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <h4 className='font-medium text-sm'>students</h4>
                      <p className='text-lg'>
                        {results.test?.studentExists ? '✅' : '❌'}
                      </p>
                    </div>
                    <div
                      className={`border rounded-lg p-4 text-center ${
                        results.test?.userExists
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <h4 className='font-medium text-sm'>users</h4>
                      <p className='text-lg'>
                        {results.test?.userExists ? '✅' : '❌'}
                      </p>
                    </div>
                  </div>

                  {/* الحالة العامة */}
                  <div
                    className={`border rounded-lg p-4 text-center ${
                      results.test?.allGood
                        ? 'bg-green-50 border-green-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <h3 className='font-medium text-lg'>
                      {results.test?.allGood
                        ? '🎉 الكود يعمل بشكل مثالي'
                        : '⚠️ الكود يحتاج إصلاح'}
                    </h3>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* نتائج الإصلاح */}
          {fixResults && (
            <div className='mb-6'>
              <h2 className='text-lg font-semibold text-gray-800 mb-4'>
                🔧 نتائج الإصلاح
              </h2>

              {fixResults.error ? (
                <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                  <p className='text-red-800'>
                    ❌ خطأ في الإصلاح: {fixResults.error}
                  </p>
                </div>
              ) : (
                <div
                  className={`border rounded-lg p-4 ${
                    fixResults.fix?.success
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <h3 className='font-medium mb-2'>
                    {fixResults.fix?.success
                      ? '✅ تم الإصلاح بنجاح'
                      : '❌ فشل في الإصلاح'}
                  </h3>
                  <p className='text-sm'>{fixResults.fix?.message}</p>
                  {fixResults.fix?.details && (
                    <div className='mt-2 text-xs bg-gray-100 p-2 rounded'>
                      <strong>التفاصيل:</strong>
                      <pre>
                        {JSON.stringify(fixResults.fix.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* إرشادات */}
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
            <h3 className='font-medium text-blue-800 mb-2'>
              💡 كيفية الاستخدام
            </h3>
            <ul className='text-sm text-blue-700 space-y-1'>
              <li>• أدخل كود الطالب واضغط "اختبار الكود" لفحص الحالة</li>
              <li>• إذا كان هناك مشكلة، اضغط "إصلاح الكود" للإصلاح التلقائي</li>
              <li>• تأكد من أن جميع المجموعات تظهر ✅ للتأكد من عمل الكود</li>
              <li>• يمكنك فتح وحدة التحكم للحصول على تفاصيل أكثر</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeDebugPage;
